/**
 * Ungesendete Kommentar-Entwürfe (#137) — je Projekt und Kommentar-Art in sessionStorage.
 *
 * Gespeichert wird das TipTap-Dokument (JSON), nicht der serialisierte `body`: nur das Dokument
 * trägt die Namen der Erwähnten (`attrs.label`), aus `@[uuid]` allein ließe sich der Chip nicht
 * wieder aufbauen. sessionStorage, weil der Entwurf Tabwechsel, Wegnavigieren und Neuladen im
 * selben Browser-Tab überstehen soll, aber nicht länger.
 *
 * Jeder Zugriff ist abgesichert: gesperrter Speicher (Privatmodus, Richtlinie) oder volles
 * Kontingent führen nur dazu, dass kein Entwurf erhalten bleibt — wie vor #137.
 */
import type { JSONContent } from '@tiptap/react';

export type KommentarArt = 'projekt' | 'staffing';

export function entwurfSchluessel(art: KommentarArt, projectId: string): string {
  return `pv:kommentar-entwurf:${art}:${projectId}`;
}

function speicher(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Liefert den Entwurf oder `null`; ein unlesbarer Eintrag wird dabei entfernt. */
export function leseEntwurf(schluessel: string): JSONContent | null {
  const s = speicher();
  if (!s) return null;
  try {
    const roh = s.getItem(schluessel);
    if (roh === null) return null;
    const doc: unknown = JSON.parse(roh);
    if (
      typeof doc === 'object' &&
      doc !== null &&
      (doc as JSONContent).type === 'doc' &&
      Array.isArray((doc as JSONContent).content)
    ) {
      return doc as JSONContent;
    }
    s.removeItem(schluessel);
    return null;
  } catch {
    try {
      s.removeItem(schluessel);
    } catch {
      // Speicher gesperrt — nichts zu tun.
    }
    return null;
  }
}

export function schreibeEntwurf(schluessel: string, doc: JSONContent): void {
  try {
    speicher()?.setItem(schluessel, JSON.stringify(doc));
  } catch {
    // Kontingent voll oder Speicher gesperrt — Entwurf bleibt dann nur im Editor.
  }
}

export function loescheEntwurf(schluessel: string): void {
  try {
    speicher()?.removeItem(schluessel);
  } catch {
    // Speicher gesperrt — nichts zu tun.
  }
}
